import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, isAdmin, ROLE_LABELS } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Users as UsersIcon, Plus, PencilSimple, Trash, Spinner, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["company_admin", "manager", "employee", "read_only"];

export default function Users() {
  const { user } = useAuth();
  const admin = isAdmin(user.role);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", active: true });
  const [delId, setDelId] = useState(null);

  const load = () => { setLoading(true); api.get("/users").then(({ data }) => { setUsers(data); setLoading(false); }); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "employee", active: true }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, active: u.active }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/users/${editing.id}`, form);
      else await api.post("/users", form);
      toast.success("User saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/users/${delId}`); toast.success("User removed"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDelId(null); }
  };

  if (!admin) return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="users-noaccess">
      <Lock size={40} className="text-zinc-300 mb-3" />
      <p className="font-semibold">Admin access required</p>
      <p className="text-sm text-zinc-500">You don't have permission to manage users.</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Users</h1><p className="text-zinc-500 text-sm mt-1">Manage team members and their roles.</p></div>
        <Button onClick={openNew} className="h-11 gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="add-user"><Plus size={18} weight="bold" /> Add User</Button>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
        {loading ? <div className="flex justify-center py-16"><Spinner size={28} className="animate-spin text-emerald-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-zinc-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-semibold text-sm">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-md">{ROLE_LABELS[u.role]}</Badge></TableCell>
                  <TableCell>{u.active ? <span className="text-emerald-600 text-sm font-medium">Active</span> : <span className="text-zinc-400 text-sm">Disabled</span>}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></Button>
                      {u.id !== user.id && u.role !== "super_admin" && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setDelId(u.id)} data-testid={`del-user-${u.id}`}><Trash size={16} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="role-legend">
        <h3 className="font-display font-bold text-sm mb-3">Role permissions</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          <div><Badge variant="outline" className="rounded-md mb-1">Company Admin</Badge><p className="text-zinc-500 text-xs leading-relaxed">Full access — manage users, company settings & billing, plus everything below.</p></div>
          <div><Badge variant="outline" className="rounded-md mb-1">Manager</Badge><p className="text-zinc-500 text-xs leading-relaxed">Manage inventory, create/edit/delete locations, and delete assets. No users, settings or billing.</p></div>
          <div><Badge variant="outline" className="rounded-md mb-1">Employee</Badge><p className="text-zinc-500 text-xs leading-relaxed">Add & edit assets, move, change status, notes, photos, VIN auto-fill & AI. Cannot delete assets or manage the team.</p></div>
          <div><Badge variant="outline" className="rounded-md mb-1">Read Only</Badge><p className="text-zinc-500 text-xs leading-relaxed">View-only access to dashboards, inventory, scanning and reports. Cannot make changes.</p></div>
        </div>
        <p className="text-xs text-zinc-400 mt-4">Only Company Admins can add, edit or remove users on this page.</p>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-sm font-semibold">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="user-name" /></div>
            <div><Label className="text-sm font-semibold">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="user-email" /></div>
            <div><Label className="text-sm font-semibold">{editing ? "New password (leave blank to keep)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "••••••" : "Default: Welcome@123"} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="user-password" /></div>
            <div><Label className="text-sm font-semibold">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-11 mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between"><Label className="text-sm font-semibold">Active</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-11 border-zinc-300">Cancel</Button>
            <Button onClick={save} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="save-user">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove user?</AlertDialogTitle><AlertDialogDescription>This user will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
